import { FunctionComponent } from 'react';
import { useMarketplace } from 'hooks';
import moment from 'moment';
import styles from './ViewEvaluationReport.module.scss';

// Models & Types
import { AppointmentPet } from '@spike/appointment-model';
import { EvaluationReport } from '@spike/report-model';
import Pet from '@spike/pet-model';

// UI Components
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { Label } from 'components/UI/V3/Forms/Label/Label';

// Internal Components
import { ViewEvaluationReportHeader } from './ViewEvaluationReportHeader';
import { EvaluationResult } from './EvaluationResult';
import PetInfo from 'components/OrderDetail/PetDetail/PetInfo';

export interface ViewEvaluationReportProps {
    evaluationReport: EvaluationReport;
    pet: AppointmentPet;
    lastResult?: boolean;
    onBack: () => void;
    onEdit: () => void;
}

const QUESTION_ANSWERS: {
    key: string;
    label: string;
}[] = [
    { key: 'pass', label: 'Pass' },
    { key: 'fail', label: 'Fail' },
    { key: 'conditional_pass', label: 'Conditional Pass' }
];

export const ViewEvaluationReport: FunctionComponent<ViewEvaluationReportProps> = props => {
    const marketplace = useMarketplace();
    const timeZone = marketplace.schedule.timeZone;
    const createAt = moment(props.evaluationReport.bookedAt);
    const updatedAt = moment(props.evaluationReport.updatedAt);
    const createAtFormat = createAt.tz(timeZone).format('MMM DD, YYYY [at] HH:mm A').toString();
    const updatedAtFormat = updatedAt.tz(timeZone).format('MMM DD, YYYY [at] HH:mm A').toString();

    return (
        <div className={styles.container}>
            <div className={styles.headerContainer}>
                <ViewEvaluationReportHeader
                    title={props.lastResult ? 'View Last Results' : 'View Results'}
                    onBack={props.onBack}
                    onEdit={props.onEdit}
                />
            </div>
            <OverlayScrollbarsComponent className={styles.scrollContainer}>
                <div className={styles.petInfoContainer}>
                    <PetInfo pet={props.pet as unknown as Pet} />
                </div>
                <div className={styles.datesContainer}>
                    <div className={styles.dateInfo}>
                        <Label className={styles.dateLabel}>Created On</Label>
                        <span className={styles.dateValue}>{createAtFormat}</span>
                    </div>
                    <div className={styles.dateInfo}>
                        <Label className={styles.dateLabel}>Last Edit</Label>
                        <span className={styles.dateValue}>
                            {createAtFormat !== updatedAtFormat ? updatedAtFormat : '-'}
                        </span>
                    </div>
                </div>
                {props.evaluationReport.customQuestionEnabled && (
                    <div className={styles.customQuestionContainer}>
                        {props.evaluationReport.customQuestionResponse.map((q, idx) => (
                            <div
                                key={q.customQuestionId}
                                className={styles.customQuestion}
                            >
                                <Label className={styles.questionLabel}>{q.questionSnapshot}</Label>
                                <span className={styles.questionResponse}>
                                    {QUESTION_ANSWERS.find(question => question.key === q.answer)?.label || '—'}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                <EvaluationResult evaluationReport={props.evaluationReport} />

                <div className={styles.notesContainer}>
                    <Label className={styles.dateLabel}>Evaluation Notes</Label>
                    <span className={styles.dateValue}>{props.evaluationReport.notes}</span>
                </div>
            </OverlayScrollbarsComponent>
        </div>
    );
};
